/**
 * PayPalSubscriptionButton Component
 *
 * Renders PayPal subscription button for recurring payments.
 * Uses PayPal Subscriptions API with a pre-configured Plan ID.
 *
 * Features:
 * - Creates PayPal subscriptions using Plan ID
 * - Handles subscription approval and activation
 * - Supports enabled/disabled state based on terms acceptance
 *
 * @validates Requirements for recurring subscriptions
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  PAYPAL_BUTTON_STYLE,
} from '../../hooks/usePayPalScript';

/**
 * PayPal subscription data returned after successful subscription
 */
export interface PayPalSubscriptionData {
  subscriptionId: string;
  planId: string;
  status: string;
  payerEmail?: string;
  payerName?: string;
}

/**
 * Props for the PayPalSubscriptionButton component
 */
export interface PayPalSubscriptionButtonProps {
  /** User ID to link the subscription to */
  userId: string;
  /** PayPal Plan ID for the subscription */
  planId: string;
  /** Callback when subscription is successfully created */
  onSuccess: (subscriptionData: PayPalSubscriptionData) => void;
  /** Callback when user cancels the subscription */
  onCancel: () => void;
  /** Callback when an error occurs */
  onError: (error: Error) => void;
  /** Whether the button is enabled (terms accepted) */
  enabled: boolean;
}

/**
 * Type for the PayPal global object with subscriptions support
 */
interface PayPalNamespace {
  Buttons: (config: PayPalSubscriptionButtonsConfig) => PayPalButtonsInstance;
}

/**
 * PayPal Buttons configuration for subscriptions
 */
interface PayPalSubscriptionButtonsConfig {
  style?: {
    layout?: 'vertical' | 'horizontal';
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
    shape?: 'rect' | 'pill';
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'subscribe';
  };
  createSubscription: (
    data: Record<string, unknown>,
    actions: PayPalSubscriptionActions
  ) => Promise<string>;
  onApprove: (
    data: PayPalSubscriptionApproveData,
    actions: PayPalSubscriptionActions
  ) => Promise<void>;
  onCancel?: (data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
}

/**
 * PayPal subscription actions
 */
interface PayPalSubscriptionActions {
  subscription: {
    create: (subscriptionData: PayPalSubscriptionCreateData) => Promise<string>;
  };
}

/**
 * PayPal subscription create data
 */
interface PayPalSubscriptionCreateData {
  plan_id: string;
  custom_id?: string;
  application_context?: {
    brand_name?: string;
    user_action?: string;
    locale?: string;
    shipping_preference?: string;
  };
}

/**
 * PayPal subscription approve callback data
 */
interface PayPalSubscriptionApproveData {
  subscriptionID: string;
  orderID?: string;
  payerID?: string;
}

/**
 * PayPal Buttons instance
 */
interface PayPalButtonsInstance {
  render: (container: HTMLElement | string) => Promise<void>;
  close: () => void;
}

/**
 * Get the PayPal global namespace
 */
function getPayPal(): PayPalNamespace | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).paypal || null;
}

/**
 * PayPalSubscriptionButton Component
 *
 * Renders PayPal subscription buttons when enabled and the SDK is loaded.
 * The button is hidden when disabled (terms not accepted).
 */
export function PayPalSubscriptionButton({
  userId,
  planId,
  onSuccess,
  onCancel,
  onError,
  enabled,
}: PayPalSubscriptionButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Create PayPal subscription with the Plan ID
   */
  const createSubscription = useCallback(
    async (
      _data: Record<string, unknown>,
      actions: PayPalSubscriptionActions
    ): Promise<string> => {
      return actions.subscription.create({
        plan_id: planId,
        custom_id: userId, // Link subscription to user
        application_context: {
          brand_name: 'Spellion',
          user_action: 'SUBSCRIBE_NOW',
          locale: 'fr-FR',
          shipping_preference: 'NO_SHIPPING',
        },
      });
    },
    [planId, userId]
  );

  /**
   * Handle successful subscription approval
   */
  const handleApprove = useCallback(
    async (data: PayPalSubscriptionApproveData): Promise<void> => {
      try {
        // Call success callback with subscription data
        onSuccess({
          subscriptionId: data.subscriptionID,
          planId: planId,
          status: 'ACTIVE',
        });
      } catch (error) {
        console.error('PayPal subscription approval error:', error);
        onError(
          error instanceof Error
            ? error
            : new Error('Erreur lors de l\'activation de l\'abonnement')
        );
      }
    },
    [planId, onSuccess, onError]
  );

  /**
   * Handle subscription cancellation
   */
  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /**
   * Handle PayPal errors
   */
  const handleError = useCallback(
    (error: Error) => {
      console.error('PayPal subscription error:', error);
      onError(error);
    },
    [onError]
  );

  /**
   * Render PayPal subscription buttons when enabled and SDK is loaded
   */
  useEffect(() => {
    // Don't render if disabled or no container or no planId
    if (!enabled || !containerRef.current || !planId) {
      return;
    }

    const paypal = getPayPal();
    if (!paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    // Clear container content before rendering new buttons
    const container = containerRef.current;
    container.innerHTML = '';

    // Create PayPal subscription buttons instance
    const buttonsConfig: PayPalSubscriptionButtonsConfig = {
      style: {
        ...PAYPAL_BUTTON_STYLE,
        label: 'subscribe',
      },
      createSubscription,
      onApprove: handleApprove,
      onCancel: handleCancel,
      onError: handleError,
    };

    let buttonsInstance: PayPalButtonsInstance | null = null;

    try {
      buttonsInstance = paypal.Buttons(buttonsConfig);

      // Render buttons to the container
      if (container && buttonsInstance) {
        buttonsInstance
          .render(container)
          .catch((err: Error) => {
            console.error('PayPal subscription button render error:', err);
          });
      }
    } catch (error) {
      console.error('PayPal Subscription Buttons initialization error:', error);
    }

    // Cleanup function
    return () => {
      if (buttonsInstance) {
        try {
          buttonsInstance.close();
        } catch {
          // Ignore errors during cleanup
        }
      }
      // Clear container on cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [enabled, planId, createSubscription, handleApprove, handleCancel, handleError]);

  // Hide the button when not enabled (terms not accepted)
  if (!enabled) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="w-full min-h-[150px]"
      data-testid="paypal-subscription-button-container"
      aria-label="Bouton d'abonnement PayPal"
    />
  );
}

export default PayPalSubscriptionButton;
